import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Typography } from "../components/atoms/Typography";
import { GameRuleKbSectionsTable } from "../components/organisms/GameRuleKbSectionsTable";
import { useGameRuleKbSections } from "../hooks/useGameRuleKbSections";
import type { KbSectionListRow } from "../types/gameRuleKbSection";

export const GameRuleKbSectionsPage = () => {
  const navigate = useNavigate();
  const {
    rows,
    loading,
    error,
    page,
    pageSize,
    totalRows,
    searchInput,
    tagFilter,
    tagOptions,
    tagsLoading,
    setSearchInput,
    setTagFilter,
    setPage,
    setPageSize,
  } = useGameRuleKbSections();

  const handleView = useCallback(
    (row: KbSectionListRow) => {
      navigate(`/rules/sections/${encodeURIComponent(row.sectionGroupId)}`);
    },
    [navigate],
  );

  return (
    <section className="page-section flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/rules-selection")}
            className="back-link"
            aria-label="Back to Game Rule"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
          </button>
          <div>
            <Typography as="h1" variant="h1">
              Game Rule
            </Typography>
            <p className="page-subtitle">
              KB clause sections for game rule generation. Each section is identified by name and
              tag.
            </p>
          </div>
        </div>
      </div>

      <div className="page-toolbar mt-6 shrink-0">
        <div className="relative max-w-xl flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="search"
            placeholder="Search by section name or tag…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <select
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          disabled={tagsLoading}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
          aria-label="Filter by tag"
        >
          <option value="">All tags</option>
          {tagOptions.map((tag) => (
            <option key={tag.id} value={tag.name}>
              {tag.name}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <div className="mt-4 shrink-0 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <div className="mt-4 min-h-0 flex-1">
        <GameRuleKbSectionsTable
          rows={rows}
          totalRows={totalRows}
          isLoading={loading}
          page={page}
          perPage={pageSize}
          onChangePage={setPage}
          onChangeRowsPerPage={setPageSize}
          onView={handleView}
        />
      </div>
    </section>
  );
};
